import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

const BOTTOM_THRESHOLD_PX = 48
const PROGRAMMATIC_SCROLL_TIMEOUT_MS = 600

function distanceFromBottom(el: HTMLElement): number {
  return el.scrollHeight - el.scrollTop - el.clientHeight
}

/**
 * Distance-from-bottom check used to decide whether the user is "pinned" to
 * the live edge of the transcript.
 */
function isNearBottom(el: HTMLElement): boolean {
  return distanceFromBottom(el) <= BOTTOM_THRESHOLD_PX
}

/**
 * A `smooth` scroll is a compositor-driven animation that browsers pause (not
 * cancel) while the tab is backgrounded, so it can still be mid-flight —
 * short of the bottom — when the fallback timeout below fires and reads the
 * DOM position, wrongly concluding the user scrolled away and un-pinning
 * auto-scroll for good. An instant (`auto`) scroll completes synchronously
 * regardless of tab visibility, so it never leaves that gap.
 */
function scrollBehaviorFor(defaultBehavior: ScrollBehavior): ScrollBehavior {
  return document.hidden ? 'auto' : defaultBehavior
}

export interface UseAutoScrollOptions {
  /**
   * Notified whenever the "user has scrolled away from the bottom" state
   * changes, so callers can toggle a jump-to-live affordance without wiring
   * their own scroll listeners (which would race with this hook's pinning).
   */
  onAwayFromBottomChange?: (isAway: boolean) => void
  /**
   * Observe the first content element for asynchronous height changes and
   * keep following it while pinned. Intended for streaming UIs whose DOM
   * keeps growing after the source items array has already been committed.
   */
  observeContentResize?: boolean
}

export interface UseAutoScroll {
  /** Ref to attach to the scrollable element. */
  ref: React.RefObject<HTMLDivElement>
  /**
   * Smooth-scroll to the live edge and re-enable auto-follow. Safe to call from
   * a click handler; it re-pins the container so subsequent content keeps
   * following even though the browser emits "not near bottom" scroll events
   * during the smooth animation.
   */
  jumpToLive: () => void
  /**
   * Read-only: true while this hook is driving a programmatic (auto or
   * jump-to-live) scroll on the element. Lets a caller that also observes raw
   * `scroll` events on the same element (e.g. a proportional scroll-sync
   * between two panes) ignore the intermediate positions of that animation
   * instead of treating them as the user manually scrolling.
   */
  isAutoScrollingRef: React.RefObject<boolean>
}

/**
 * Auto-scroll a streaming-content container to its bottom while the user has
 * not manually scrolled away. Keeps the container pinned across token updates,
 * ignores scroll events emitted by its own smooth-scroll animation (which would
 * otherwise unpin mid-animation and stall further auto-scrolls), and exposes an
 * imperative {@link UseAutoScroll.jumpToLive} that re-pins so a jump-to-live
 * button stays in sync with the hook's follow state.
 *
 * @param items    Array whose reference change signals new content; only its
 *                 identity is observed, not the elements themselves.
 * @param options  Optional callbacks; see {@link UseAutoScrollOptions}.
 * @returns Ref to attach plus an imperative jump-to-live handler.
 */
export default function useAutoScroll<T>(
  items: T[],
  options: UseAutoScrollOptions = {}
): UseAutoScroll {
  const { onAwayFromBottomChange, observeContentResize = false } = options
  const ref = useRef<HTMLDivElement>(null)
  const isPinnedToBottomRef = useRef(true)
  const previousLengthRef = useRef(0)
  const programmaticScrollRef = useRef(false)
  const programmaticTimerRef = useRef<number | null>(null)
  // Per-instance scroll position/height so intentional upward scrolls unpin
  // even within BOTTOM_THRESHOLD_PX, while scrollHeight shrink clamps do not.
  const lastScrollTopRef = useRef(0)
  const lastScrollHeightRef = useRef(0)
  // Distinguishes panel hide/show (container width reflow) from streaming
  // content growth when `observeContentResize` is on.
  const lastClientWidthRef = useRef(0)
  const lastClientHeightRef = useRef(0)
  // Element currently holding listeners — tracks collapse/expand remounts even
  // when `items.length > 0` never flips (the old effect dep missed remounts).
  const attachedElRef = useRef<HTMLDivElement | null>(null)
  const detachListenersRef = useRef<(() => void) | null>(null)
  const itemsLengthRef = useRef(items.length)
  itemsLengthRef.current = items.length
  // Bumps when the scroll node attaches so content-resize observation rebinds.
  const [elementEpoch, setElementEpoch] = useState(0)

  // Keep the latest callback in a ref so the scroll-listener effect does not
  // re-subscribe (and momentarily drop events) on every render.
  const onAwayChangeRef = useRef(onAwayFromBottomChange)
  onAwayChangeRef.current = onAwayFromBottomChange
  const lastAwayRef = useRef(false)

  /**
   * Recompute pinning from the live DOM position and notify the caller only
   * when the away-from-bottom state actually flips, avoiding redundant renders.
   */
  const syncPinnedState = useCallback((el: HTMLElement) => {
    const near = isNearBottom(el)
    isPinnedToBottomRef.current = near
    const isAway = !near
    if (isAway !== lastAwayRef.current) {
      lastAwayRef.current = isAway
      onAwayChangeRef.current?.(isAway)
    }
  }, [])

  const jumpToLive = useCallback(() => {
    const el = ref.current
    if (!el) return

    // Re-pin before the animation starts and guard the scroll events it emits,
    // so mid-flight "not near bottom" reports don't unpin us again.
    isPinnedToBottomRef.current = true
    if (lastAwayRef.current) {
      lastAwayRef.current = false
      onAwayChangeRef.current?.(false)
    }
    programmaticScrollRef.current = true
    el.scrollTo({ top: el.scrollHeight, behavior: scrollBehaviorFor('smooth') })

    if (programmaticTimerRef.current !== null) {
      window.clearTimeout(programmaticTimerRef.current)
    }
    const finish = () => {
      if (isPinnedToBottomRef.current) {
        el.scrollTo({ top: el.scrollHeight, behavior: 'auto' })
      }
      lastScrollTopRef.current = el.scrollTop
      lastScrollHeightRef.current = el.scrollHeight
      programmaticScrollRef.current = false
      el.removeEventListener('scrollend', finish)
      if (programmaticTimerRef.current !== null) {
        window.clearTimeout(programmaticTimerRef.current)
        programmaticTimerRef.current = null
      }
    }
    if ('onscrollend' in el) {
      el.addEventListener('scrollend', finish, { once: true })
    }
    programmaticTimerRef.current = window.setTimeout(finish, PROGRAMMATIC_SCROLL_TIMEOUT_MS)
  }, [])

  /**
   * Bind scroll/wheel/layout listeners to a newly mounted scroll node.
   * Expand/collapse remounts must go through here — never scrollTo bottom solely
   * because the element attached (that is a layout change, not follow intent).
   */
  const attachToElement = useCallback(
    (el: HTMLDivElement) => {
      lastScrollTopRef.current = el.scrollTop
      lastScrollHeightRef.current = el.scrollHeight
      lastClientWidthRef.current = el.clientWidth
      lastClientHeightRef.current = el.clientHeight
      // Tokens may have streamed while collapsed; keep length in sync so the
      // next items update is not mistaken for a 0→N growth scroll.
      previousLengthRef.current = itemsLengthRef.current

      // Fresh mount at the top of an already-long transcript: unpin + show Jump
      // to Live. Do not scrollTo — expand is layout-only.
      if (itemsLengthRef.current > 0 && !isNearBottom(el)) {
        isPinnedToBottomRef.current = false
        if (!lastAwayRef.current) {
          lastAwayRef.current = true
          onAwayChangeRef.current?.(true)
        }
      } else {
        syncPinnedState(el)
      }

      const onScroll = () => {
        const scrollTop = el.scrollTop
        const scrollHeight = el.scrollHeight
        const prevTop = lastScrollTopRef.current
        const prevHeight = lastScrollHeightRef.current
        const heightShrunk = scrollHeight < prevHeight

        if (programmaticScrollRef.current) {
          lastScrollTopRef.current = scrollTop
          lastScrollHeightRef.current = scrollHeight
          return
        }

        // Intentional upward scroll: unpin immediately even within the 48px
        // near-bottom threshold. Only ignore the upward delta when height
        // shrank and the viewport is still flush with the live edge (clamp).
        if (scrollTop < prevTop) {
          lastScrollTopRef.current = scrollTop
          lastScrollHeightRef.current = scrollHeight
          if (heightShrunk && isNearBottom(el)) {
            return
          }
          isPinnedToBottomRef.current = false
          if (!lastAwayRef.current) {
            lastAwayRef.current = true
            onAwayChangeRef.current?.(true)
          }
          return
        }

        lastScrollTopRef.current = scrollTop
        lastScrollHeightRef.current = scrollHeight

        if (heightShrunk) {
          return
        }

        syncPinnedState(el)
      }

      const onUserInput = () => {
        if (!programmaticScrollRef.current) return
        programmaticScrollRef.current = false
        syncPinnedState(el)
      }

      const onWheel = () => {
        programmaticScrollRef.current = false
        if (!isNearBottom(el)) {
          isPinnedToBottomRef.current = false
          if (!lastAwayRef.current) {
            lastAwayRef.current = true
            onAwayChangeRef.current?.(true)
          }
        }
      }

      let resyncFrame: number | null = null
      const cancelResync = () => {
        if (resyncFrame !== null) {
          window.cancelAnimationFrame(resyncFrame)
          resyncFrame = null
        }
      }
      const onVisibilityChange = () => {
        if (document.hidden) {
          cancelResync()
          if (programmaticScrollRef.current) {
            el.scrollTo({ top: el.scrollHeight, behavior: 'auto' })
          }
          return
        }
        if (!isPinnedToBottomRef.current) return
        cancelResync()
        resyncFrame = window.requestAnimationFrame(() => {
          resyncFrame = window.requestAnimationFrame(() => {
            resyncFrame = null
            if (document.hidden || !isPinnedToBottomRef.current) return
            el.scrollTo({ top: el.scrollHeight, behavior: 'auto' })
          })
        })
      }

      const userInputEvents = ['touchstart', 'mousedown', 'keydown'] as const
      userInputEvents.forEach((ev) => {
        el.addEventListener(ev, onUserInput, { passive: true })
      })
      el.addEventListener('wheel', onWheel, { passive: true })
      el.addEventListener('scroll', onScroll, { passive: true })
      document.addEventListener('visibilitychange', onVisibilityChange)

      let layoutObserver: ResizeObserver | null = null
      if (typeof ResizeObserver !== 'undefined') {
        layoutObserver = new ResizeObserver(() => {
          const prevHeight = lastClientHeightRef.current
          lastScrollTopRef.current = el.scrollTop
          lastScrollHeightRef.current = el.scrollHeight
          lastClientWidthRef.current = el.clientWidth
          lastClientHeightRef.current = el.clientHeight

          // Keep-alive pane inside a collapsed (≈0px) column just gained a real
          // viewport — treat like late attach: sync away from DOM, never yank.
          if (prevHeight < 8 && el.clientHeight >= 8) {
            if (itemsLengthRef.current > 0 && !isNearBottom(el)) {
              isPinnedToBottomRef.current = false
              if (!lastAwayRef.current) {
                lastAwayRef.current = true
                onAwayChangeRef.current?.(true)
              }
            } else {
              syncPinnedState(el)
            }
            return
          }

          if (!isPinnedToBottomRef.current) {
            syncPinnedState(el)
            return
          }

          // Quietly re-stick if reflow left a gap. Do not arm programmaticScrollRef.
          if (!isNearBottom(el)) {
            el.scrollTo({ top: el.scrollHeight, behavior: 'auto' })
            lastScrollTopRef.current = el.scrollTop
            lastScrollHeightRef.current = el.scrollHeight
          }
        })
        layoutObserver.observe(el)
      }

      detachListenersRef.current = () => {
        userInputEvents.forEach((ev) => {
          el.removeEventListener(ev, onUserInput)
        })
        el.removeEventListener('wheel', onWheel)
        el.removeEventListener('scroll', onScroll)
        document.removeEventListener('visibilitychange', onVisibilityChange)
        layoutObserver?.disconnect()
        cancelResync()
      }
    },
    [syncPinnedState]
  )

  // Rebind whenever the scroll node identity changes (collapse unmount / expand
  // remount). Runs after every commit but no-ops when the attached node is stable.
  useLayoutEffect(() => {
    const el = ref.current
    if (el === attachedElRef.current) return

    detachListenersRef.current?.()
    detachListenersRef.current = null
    attachedElRef.current = el

    if (el) {
      attachToElement(el)
      setElementEpoch((epoch) => epoch + 1)
    }
  })

  useEffect(() => {
    return () => {
      detachListenersRef.current?.()
      detachListenersRef.current = null
      attachedElRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!observeContentResize || typeof ResizeObserver === 'undefined') return

    const el = ref.current
    const content = el?.firstElementChild
    if (!el || !content) return

    const requestFrame = window.requestAnimationFrame.bind(window)
    const cancelFrame = window.cancelAnimationFrame.bind(window)
    let scrollFrame: number | null = null
    let releaseFrame: number | null = null
    lastClientWidthRef.current = el.clientWidth
    const observer = new ResizeObserver(() => {
      const clientWidth = el.clientWidth
      const layoutReflow =
        lastClientWidthRef.current > 0 && clientWidth !== lastClientWidthRef.current
      lastClientWidthRef.current = clientWidth

      // Width-driven reflow: refresh baselines only — do not arm programmatic follow.
      if (layoutReflow) {
        lastScrollTopRef.current = el.scrollTop
        lastScrollHeightRef.current = el.scrollHeight
        return
      }

      if (!isPinnedToBottomRef.current) return

      if (scrollFrame !== null) {
        cancelFrame(scrollFrame)
      }
      scrollFrame = requestFrame(() => {
        scrollFrame = null
        if (!isPinnedToBottomRef.current) return

        programmaticScrollRef.current = true
        el.scrollTo({ top: el.scrollHeight, behavior: 'auto' })

        if (releaseFrame !== null) {
          cancelFrame(releaseFrame)
        }
        releaseFrame = requestFrame(() => {
          releaseFrame = null
          if (isPinnedToBottomRef.current) {
            el.scrollTo({ top: el.scrollHeight, behavior: 'auto' })
          }
          lastScrollTopRef.current = el.scrollTop
          lastScrollHeightRef.current = el.scrollHeight
          programmaticScrollRef.current = false
        })
      })
    })

    observer.observe(content)

    return () => {
      observer.disconnect()
      if (scrollFrame !== null) {
        cancelFrame(scrollFrame)
      }
      if (releaseFrame !== null) {
        cancelFrame(releaseFrame)
      }
    }
  }, [items.length, observeContentResize, syncPinnedState, elementEpoch])

  useEffect(() => {
    if (!items.length) {
      isPinnedToBottomRef.current = true
      previousLengthRef.current = 0
      if (lastAwayRef.current) {
        lastAwayRef.current = false
        onAwayChangeRef.current?.(false)
      }
      return
    }

    const el = ref.current
    if (!el) {
      // Pane collapsed / scroll DOM detached — still track length so expand
      // does not look like a 0→N growth that yanks to the bottom.
      previousLengthRef.current = items.length
      return
    }

    if (!isPinnedToBottomRef.current) {
      previousLengthRef.current = items.length
      return
    }

    // Clear the away indicator the moment we commit to auto-following, not
    // once the animation later settles — a steady stream of new items keeps
    // re-targeting the scroll before `finish` below ever gets to run.
    if (lastAwayRef.current) {
      lastAwayRef.current = false
      onAwayChangeRef.current?.(false)
    }

    const behavior = scrollBehaviorFor(
      items.length > previousLengthRef.current ? 'smooth' : 'auto'
    )

    programmaticScrollRef.current = true
    el.scrollTo({ top: el.scrollHeight, behavior })
    previousLengthRef.current = items.length

    if (programmaticTimerRef.current !== null) {
      window.clearTimeout(programmaticTimerRef.current)
    }

    const finish = () => {
      if (isPinnedToBottomRef.current) {
        el.scrollTo({ top: el.scrollHeight, behavior: 'auto' })
      }
      lastScrollTopRef.current = el.scrollTop
      lastScrollHeightRef.current = el.scrollHeight
      programmaticScrollRef.current = false
      el.removeEventListener('scrollend', finish)
      if (programmaticTimerRef.current !== null) {
        window.clearTimeout(programmaticTimerRef.current)
        programmaticTimerRef.current = null
      }
    }

    if ('onscrollend' in el) {
      el.addEventListener('scrollend', finish, { once: true })
    }
    programmaticTimerRef.current = window.setTimeout(finish, PROGRAMMATIC_SCROLL_TIMEOUT_MS)
  }, [items])

  return { ref, jumpToLive, isAutoScrollingRef: programmaticScrollRef }
}
